// src/lib/emailSession.js
//
// Email sign-in (mission 007, decision 02): Supabase Auth email OTP proves the
// email; the email-session Edge function turns its access token into the
// Lunexx JWT for the account's managed address (authService.setEmailSession).
// A NEW account needs explicit consent (create_account: true after the
// separate-accounts disclosure, Astra 02 F1); a silent restore never creates one.

import { getSupabaseAuth } from './supabaseAuth';
import { authService, storedReferralCode } from '../services/AuthService';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const normalizeEmail = (e) => String(e ?? '').trim().toLowerCase();

export async function sendEmailCode(email) {
  const { error } = await getSupabaseAuth().auth.signInWithOtp({ email, options: { shouldCreateUser: true } });
  if (error) throw new Error(error.message || 'Could not send the code. Try again.');
}

/** → the Supabase Auth access token */
export async function verifyEmailCode(email, code) {
  const { data, error } = await getSupabaseAuth().auth.verifyOtp({ email, token: code, type: 'email' });
  const token = data?.session?.access_token;
  if (error || !token) throw new Error(error?.message || 'That code did not work. Check it or request a new one.');
  return token;
}

/** POST email-session → { needs_account, email } or the session ({ token, walletAddress, expiresIn, user, email }) */
export async function requestEmailSession(accessToken, { createAccount = false } = {}) {
  const body = { access_token: accessToken, referral_code: storedReferralCode() };
  if (createAccount) body.create_account = true;
  const res = await fetch(`${SUPABASE_URL}/functions/v1/email-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${SUPABASE_ANON_KEY}`, apikey: SUPABASE_ANON_KEY },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(json?.error || `Sign-in failed (${res.status})`);
    e.code = json?.code;
    throw e;
  }
  return json;
}

let restoring = null;
/** a persisted Supabase Auth session without a Lunexx token → the Lunexx session, silently (no code). Never creates an account. */
export function restoreEmailSession() {
  if (restoring) return restoring;
  restoring = (async () => {
    try {
      const { data } = await getSupabaseAuth().auth.getSession();
      const at = data?.session?.access_token;
      if (!at) return null;
      const r = await requestEmailSession(at);
      if (r?.needs_account || !r?.token) {
        await getSupabaseAuth().auth.signOut().catch(() => {});
        return null;
      }
      return authService.setEmailSession(r).token;
    } catch {
      return null;   // signed out; a transient failure keeps the Supabase session for the next try
    } finally {
      restoring = null;
    }
  })();
  return restoring;
}

/** ends the Lunexx session and the Supabase Auth session */
export async function endEmailSession() {
  authService.logout();
  try { await getSupabaseAuth().auth.signOut(); } catch { /* already signed out */ }
}
